import React, { useEffect, useState } from "react";
import {
  Tooltip,
  Typography,
  Grid,
  Box,
  TextareaAutosize,
} from "@mui/material";
import { formattedJson } from "../../services/utils";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import ReadMoreLess from "../ReadMoreLess";
import { useRuleResourceContext } from "../../contexts/RuleResourceContext";
import { useSystemContext } from "../../contexts/TagAndSystemProvider";
import { sqlDatabaseType } from "../../services/constants";

interface ViewLinkedServicesProps {
  resourceData: any;
  linkedServicesData: any;
  connectionKeysData: any;
  fileProcessingData: any;
  systemVisible?: boolean;
}

const ViewLinkedServices = ({
  resourceData,
  linkedServicesData,
  connectionKeysData,
  fileProcessingData,
  systemVisible = true,
}: ViewLinkedServicesProps) => {
  const { allResourcesData, fetchedConnectionKeys, fetchedAllConnectionKeys } =
    useRuleResourceContext();
  /**
   * Helper function to get connection key name with consistent logic across all resource types
   * @param connectionKeyPath - Dot-separated path to the connection key ID within resource_definition
   *                           Examples: "connection_key", "api_definition.connection_key"
   * @returns Connection key name or "N/A" if not found
   */
  const getConnectionKeyName = (connectionKeyPath: string): string => {
    // Extract connection key ID using the provided path
    const pathParts = connectionKeyPath.split(".");
    let connectionKeyId: any;
    // MAIN RESOURCE: Look in additional_properties.resource_definition
    if (resourceData?.additional_properties?.resource_definition) {
      connectionKeyId = resourceData.additional_properties.resource_definition;

      // Navigate through the path to get the connection key ID
      for (const part of pathParts) {
        connectionKeyId = connectionKeyId?.[part];
      }
    }
    // ADDITIONAL RESOURCE: Look directly in the resource data
    else {
      connectionKeyId = resourceData;

      // Navigate through the path to get the connection key ID
      for (const part of pathParts) {
        connectionKeyId = connectionKeyId?.[part];
      }
    }

    if (!connectionKeyId) {
      return "N/A";
    }

    // FIXED: Search in both connectionKeysData prop and fetchedConnectionKeys from context
    // This ensures newly fetched connection keys (from connection type changes) are found
    // First try the prop data (original connection keys)
    let connectionKey = connectionKeysData?.find(
      (key: any) => key?.id === connectionKeyId,
    );

    // If not found in prop, try the context data (newly fetched connection keys)
    if (!connectionKey && fetchedConnectionKeys) {
      connectionKey = fetchedConnectionKeys.find(
        (key: any) => key?.id === connectionKeyId,
      );
    }

    if (connectionKey) {
      return connectionKey.name;
    }

    // FALLBACK: If connection key not found, show N/A
    return "N/A";
  };
  /**
   * Helper to get system label from a connection key path.
   * Follows a direct path only:
   * connection key ID -> connection key object -> system_id -> system value.
   * If any step fails, returns "N/A".
   *
   * SYSTEM FIX: Now checks all three connection key sources (connectionKeysData,
   * fetchedAllConnectionKeys, fetchedConnectionKeys) to ensure system is found
   * even when connection key comes from newly fetched data after connection type change.
   */
  const getSystemLabelFromConnectionKey = (
    connectionKeyPath: string,
  ): string => {
    if (!Array.isArray(allSystemData) || allSystemData.length === 0) {
      return "N/A";
    }
    const pathParts = connectionKeyPath.split(".");
    let connectionKeyId: any;
    // MAIN RESOURCE: Look in additional_properties.resource_definition
    if (resourceData?.additional_properties?.resource_definition) {
      connectionKeyId = resourceData.additional_properties.resource_definition;
      for (const part of pathParts) {
        connectionKeyId = connectionKeyId?.[part];
      }
    } else {
      // ADDITIONAL RESOURCE or other shapes: Look directly in resourceData
      connectionKeyId = resourceData;
      for (const part of pathParts) {
        connectionKeyId = connectionKeyId?.[part];
      }
    }
    if (!connectionKeyId) {
      return "N/A";
    }

    // SYSTEM FIX: Search in all three connection key sources
    // 1. First try connectionKeysData (original prop)
    let connectionKey = connectionKeysData?.find(
      (key: any) => key?.id === connectionKeyId,
    );

    // 2. If not found, try fetchedAllConnectionKeys (all connection keys from context)
    if (!connectionKey && fetchedAllConnectionKeys) {
      connectionKey = fetchedAllConnectionKeys.find(
        (key: any) => key?.id === connectionKeyId,
      );
    }

    // 3. If still not found, try fetchedConnectionKeys (newly fetched connection keys)
    if (!connectionKey && fetchedConnectionKeys) {
      connectionKey = fetchedConnectionKeys.find(
        (key: any) => key?.id === connectionKeyId,
      );
    }

    if (!connectionKey || !connectionKey.system_id) {
      return "N/A";
    }
    const system = allSystemData.find(
      (item: any) => item.id === connectionKey.system_id,
    );
    return system?.value || "N/A";
  };
  const { allSystemData } = useSystemContext();
  const [storageType, setStorageType] = useState("");
  const [resourceType, setResourceType] = useState("");
  useEffect(() => {
    if (resourceData && linkedServicesData) {
      setResourceType(resourceData?.aggregation_type);
      if (resourceData?.linked_service_id) {
        let type;
        type = linkedServicesData?.find(
          (option: { id: any }) =>
            option.id === resourceData?.linked_service_id,
        )?.sub_type;
        setStorageType(type);
      }
    }
  }, [resourceData, linkedServicesData]);

  const handleChipClick = (domainId: number, option: any) => {
    window.open(
      `/resource/${domainId}/view/${option?.base_resource_id}`,
      "_blank",
    );
  };
  return (
    <>
      {resourceType === "flat" && (
        <>
          {systemVisible && (
            <Grid item xs={12} sm={6} md={4} lg={3} xl={3}>
              <label className="label-text text-bold">System</label>
              <div className="form-control word-break-all">
                {/* {getSystemLabelFromConnectionKey("connection_key")} */}
                {(allSystemData?.length > 0 &&
                  allSystemData?.find(
                    (item: any) => item.id === resourceData?.system_id,
                  )?.value) ||
                  "N/A"}
              </div>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
            <label className="label-text text-bold">
              <span className="position-relative">
                Connection Type
                {resourceData?.linked_service_id && (
                  <Tooltip
                    componentsProps={{
                      tooltip: { className: "wide-tooltip w-250" },
                    }}
                    title={
                      resourceData?.linked_service_id && (
                        <React.Fragment>
                          <Typography color="inherit">
                            Connection Type Code :{" "}
                            {
                              linkedServicesData?.find(
                                (item: any) =>
                                  item?.id === resourceData?.linked_service_id,
                              )?.code
                            }
                          </Typography>
                        </React.Fragment>
                      )
                    }
                  >
                    <InfoIcon
                      sx={{
                        position: "absolute",
                        top: "50%",
                        transform: "translateY(-50%)",
                        right: "-24px",
                        width: "16px",
                      }}
                    />
                  </Tooltip>
                )}
              </span>
            </label>
            <div className="form-control">
              {linkedServicesData?.find(
                (item: any) => item?.id === resourceData?.linked_service_id,
              )?.name || "N/A"}
            </div>
          </Grid>
          {sqlDatabaseType.includes(storageType) && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Connection Key</label>
                <div className="form-control">
                  {getConnectionKeyName("connection_key")}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">SQL Query</label>
                <div className="form-control break-word">
                  {resourceData?.additional_properties?.resource_definition
                    ?.sql_query?.length > 100 ? (
                    <ReadMoreLess
                      data={
                        resourceData.additional_properties.resource_definition
                          .sql_query
                      }
                      charLimit={100}
                    />
                  ) : (
                    resourceData?.additional_properties?.resource_definition
                      ?.sql_query || "N/A"
                  )}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">
                  Use Multi Thread Reader
                </label>
                <div className="form-control">
                  {resourceData?.additional_properties?.resource_definition
                    ?.sql_definition?.use_multi_thread_reader
                    ? "Yes"
                    : "No"}
                </div>
              </Grid>

              {resourceData?.additional_properties?.resource_definition
                ?.sql_definition?.use_multi_thread_reader && (
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Column Name to Partition On
                  </label>
                  <div className="form-control break-word">
                    {resourceData?.additional_properties?.resource_definition
                      ?.sql_definition.column_name_to_partition_on_sql_query ||
                      "N/A"}
                  </div>
                </Grid>
              )}

              {/* Parquet Export Configuration (read-only) */}
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">
                  Parquet Export Enabled
                </label>
                <div className="form-control">
                  {resourceData?.additional_properties?.resource_definition
                    ?.use_parquet_export
                    ? "Yes"
                    : "No"}
                </div>
              </Grid>

              {resourceData?.additional_properties?.resource_definition
                ?.use_parquet_export && (
                <>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Partition Column
                    </label>
                    <div className="form-control break-word">
                      {resourceData?.additional_properties?.resource_definition
                        ?.parquet_export_definition?.partition_column || "N/A"}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Partition Strategy
                    </label>
                    <div className="form-control">
                      {resourceData?.additional_properties?.resource_definition
                        ?.parquet_export_definition?.partition_strategy ||
                        "N/A"}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Number of Partitions
                    </label>
                    <div className="form-control">
                      {resourceData?.additional_properties?.resource_definition
                        ?.parquet_export_definition?.number_of_partitions ?? 1}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">Max Workers</label>
                    <div className="form-control">
                      {resourceData?.additional_properties?.resource_definition
                        ?.parquet_export_definition?.max_workers ?? 4}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">Chunk Size</label>
                    <div className="form-control">
                      {resourceData?.additional_properties?.resource_definition
                        ?.parquet_export_definition?.chunk_size ?? 50000}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Enable Compression
                    </label>
                    <div className="form-control">
                      {resourceData?.additional_properties?.resource_definition
                        ?.parquet_export_definition?.enable_compression
                        ? "Yes"
                        : "No"}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Merge Output Files
                    </label>
                    <div className="form-control">
                      {resourceData?.additional_properties?.resource_definition
                        ?.parquet_export_definition?.merge_output_files
                        ? "Yes"
                        : "No"}
                    </div>
                  </Grid>
                </>
              )}
            </>
          )}
          {["blob", "sftp", "local"].includes(storageType) && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Connection Key</label>
                <div className="form-control">
                  {getConnectionKeyName("connection_key")}
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">
                  <span className="position-relative">
                    File Processing Attribute
                    {resourceData?.file_processing_id && (
                      <Tooltip
                        componentsProps={{
                          tooltip: { className: "wide-tooltip w-380" },
                        }}
                        title={
                          <pre
                            style={{
                              whiteSpace: "pre-wrap",
                              margin: 0,
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                          >
                            <React.Fragment>
                              <Typography color="inherit">
                                File Processing Detail
                              </Typography>
                              <Typography>
                                {formattedJson(
                                  JSON.stringify(
                                    fileProcessingData?.find(
                                      (file: any) =>
                                        file?.id ===
                                        resourceData?.file_processing_id,
                                    ),
                                  ),
                                )}
                              </Typography>
                            </React.Fragment>
                          </pre>
                        }
                      >
                        <InfoIcon
                          sx={{
                            position: "absolute",
                            top: "50%",
                            transform: "translateY(-50%)",
                            right: "-24px",
                            width: "16px",
                          }}
                        />
                      </Tooltip>
                    )}
                  </span>
                </label>
                <div className="form-control">
                  {fileProcessingData?.find(
                    (file: any) =>
                      file?.id === resourceData?.file_processing_id,
                  )?.resource_type || "N/A"}
                </div>
              </Grid>
              {storageType === "sftp" ? (
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Remote Directory
                  </label>
                  <div className="form-control break-word">
                    {resourceData?.additional_properties?.resource_definition
                      ?.remote_directory || "N/A"}
                  </div>
                </Grid>
              ) : (
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">Resource Path</label>
                  <div className="form-control break-word">
                    {resourceData?.additional_properties?.resource_definition
                      ?.resource_path || "N/A"}
                  </div>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Column Delimiter</label>
                <div className="form-control">
                  {resourceData?.additional_properties?.resource_definition
                    ?.column_delimiter || "N/A"}
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">File Name</label>
                <div className="form-control word-wrap-break-word">
                  {resourceData?.additional_properties?.resource_definition
                    ?.file_name || "N/A"}
                </div>
              </Grid>
              {resourceData?.additional_properties?.resource_definition
                ?.excel_sheet_name && (
                <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                  <label className="label-text text-bold">
                    Excel Sheet Name
                  </label>
                  <div className="form-control word-wrap-break-word">
                    {resourceData?.additional_properties?.resource_definition
                      ?.excel_sheet_name || "N/A"}
                  </div>
                </Grid>
              )}
              {storageType === "blob" && (
                <>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Container Name
                    </label>
                    <div className="form-control">
                      {resourceData?.additional_properties?.resource_definition
                        ?.container_name || "N/A"}
                    </div>
                  </Grid>
                </>
              )}
            </>
          )}
          {linkedServicesData?.find(
            (option: { id: any }) =>
              option.id === resourceData?.linked_service_id,
          )?.type === "api" && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Connection Key</label>
                <div className="form-control">
                  {getConnectionKeyName("api_definition.connection_key")}
                </div>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Method</label>
                <div className="form-control word-wrap-break-word">
                  {resourceData?.additional_properties?.resource_definition
                    ?.api_definition?.method || "get"}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Content Type</label>
                <div className="form-control word-wrap-break-word">
                  {resourceData?.additional_properties?.resource_definition
                    ?.api_definition?.content_type || "application/json"}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Url</label>
                <div className="form-control word-wrap-break-word">
                  {resourceData?.additional_properties?.resource_definition
                    ?.api_definition?.url || "N/A"}
                </div>
              </Grid>

              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">
                  Request Timeout(in seconds)
                </label>
                <div className="form-control word-wrap-break-word">
                  {resourceData?.additional_properties?.resource_definition
                    ?.api_definition?.request_timeout || 0}
                </div>
              </Grid>

              {storageType === "basic_auth_api" && (
                <>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">Username</label>
                    <div className="form-control word-wrap-break-word">
                      {resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.user_name || "N/A"}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">Password</label>
                    <div className="form-control word-wrap-break-word">
                      {resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.password || "N/A"}
                    </div>
                  </Grid>
                </>
              )}
              {storageType === "token_auth_api" && (
                <>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">Bearer token</label>
                    <div className="form-control word-wrap-break-word">
                      {resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.bearer_token || "N/A"}
                    </div>
                  </Grid>
                </>
              )}
              {storageType === "key_auth_api" && (
                <>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">API key</label>
                    <div className="form-control word-wrap-break-word">
                      {resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.api_key || "N/A"}
                    </div>
                  </Grid>
                </>
              )}
              {storageType === "oauth_api" && (
                <>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Oauth Client Id
                    </label>
                    <div className="form-control word-wrap-break-word">
                      {resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.oauth_client_id || "N/A"}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">
                      Oauth Client Secret
                    </label>
                    <div className="form-control word-wrap-break-word">
                      {resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.oauth_client_secret || "N/A"}
                    </div>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                    <label className="label-text text-bold">Oauth Url</label>
                    <div className="form-control word-wrap-break-word">
                      {resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.oauth_url || "N/A"}
                    </div>
                  </Grid>
                </>
              )}
            </>
          )}
          {linkedServicesData?.find(
            (option: { id: any }) =>
              option.id === resourceData?.linked_service_id,
          )?.type === "api" && (
            <>
              {resourceData?.additional_properties?.resource_definition
                ?.api_definition?.method !== "get" && (
                <Grid item xs={12} sm={6} md={12} lg={12} xl={12}>
                  <label className="label-text text-bold">Request Body</label>
                  <div className="form-control word-wrap-break-word">
                    <TextareaAutosize
                      title="Request Body"
                      disabled={true}
                      name="body"
                      minRows={3}
                      maxRows={6}
                      className={`form-control-1`}
                      value={formattedJson(
                        resourceData?.additional_properties?.resource_definition
                          ?.api_definition?.body,
                      )}
                    />
                  </div>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Query params</label>
                <Box className="params-box">
                  <div className="avail-columns-group">
                    {resourceData?.additional_properties?.resource_definition
                      ?.api_definition?.query_params &&
                    Object.keys(
                      resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.query_params,
                    ).length > 0
                      ? Object.keys(
                          resourceData?.additional_properties
                            ?.resource_definition?.api_definition?.query_params,
                        ).map((key, index) => (
                          <Box key={index} className="avail-columns">
                            <button
                              type="button"
                              style={{
                                display: `${key === "" ? "none" : "block"}`,
                              }}
                            >
                              {key} :{" "}
                              {
                                resourceData?.additional_properties
                                  ?.resource_definition?.api_definition
                                  ?.query_params[key]
                              }
                            </button>
                          </Box>
                        ))
                      : "N/A"}
                  </div>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                <label className="label-text text-bold">Url params</label>
                <Box className="params-box">
                  <div className="avail-columns-group">
                    {resourceData?.additional_properties?.resource_definition
                      ?.api_definition?.url_params &&
                    Object.keys(
                      resourceData?.additional_properties?.resource_definition
                        ?.api_definition?.url_params,
                    ).length > 0
                      ? Object.keys(
                          resourceData?.additional_properties
                            ?.resource_definition?.api_definition?.url_params,
                        ).map((key, index) => (
                          <Box key={index} className="avail-columns">
                            <button
                              type="button"
                              style={{
                                display: `${key === "" ? "none" : "block"}`,
                              }}
                            >
                              {key} :{" "}
                              {
                                resourceData?.additional_properties
                                  ?.resource_definition?.api_definition
                                  ?.url_params[key]
                              }
                            </button>
                          </Box>
                        ))
                      : "N/A"}
                  </div>
                </Box>
              </Grid>
            </>
          )}
        </>
      )}
      {resourceType === "aggregated" && (
        <>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
            <label className="label-text text-bold">Base Resource</label>
            <div
              onClick={() =>
                handleChipClick(
                  resourceData?.domain_id,
                  resourceData?.additional_properties?.aggregation_properties,
                )
              }
              className="form-control btn-new-window"
            >
              {allResourcesData?.find(
                (item: any) =>
                  item?.id ===
                  resourceData?.additional_properties?.aggregation_properties
                    ?.base_resource_id,
              )?.resource_name || "N/A"}
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
            <label className="label-text text-bold">
              Base Resource Columns
            </label>
            <div className="form-control">
              {Array.isArray(
                resourceData?.additional_properties?.aggregation_properties
                  ?.base_resource_columns,
              )
                ? resourceData.additional_properties.aggregation_properties.base_resource_columns.join(
                    ", ",
                  )
                : "N/A"}
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
            <label className="label-text text-bold">
              Base Resource Validation
            </label>
            <div className="form-control">
              {resourceData?.additional_properties?.aggregation_properties
                ?.base_resource_validation
                ? "True"
                : "False"}
            </div>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
            <label className="label-text text-bold">Aggregation Query</label>
            <div className="form-control word-wrap-break-word">
              {resourceData?.additional_properties?.aggregation_properties
                ?.aggregation_query &&
              resourceData.additional_properties.aggregation_properties
                .aggregation_query.length > 100 ? (
                <ReadMoreLess
                  data={
                    resourceData.additional_properties.aggregation_properties
                      .aggregation_query
                  }
                  charLimit={100}
                />
              ) : (
                resourceData?.additional_properties?.aggregation_properties
                  ?.aggregation_query || "N/A"
              )}
            </div>
          </Grid>
        </>
      )}
    </>
  );
};

export default ViewLinkedServices;
